import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Drawer, DrawerContent, DrawerFooter,
} from "@/components/ui/drawer";
import { ArrowLeft, CheckCircle2, Clock, Loader2, Search, AlertTriangle, Container } from "lucide-react";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { Badge } from "@/components/ui/badge";
import { formatDwellTime, isDwellOverThreshold } from "@/lib/dwell-time";
import { toast } from "sonner";
import { useTrailerImageUrls } from "@/lib/trailer-image";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";
import { NeedsServiceToggle } from "@/components/yard/NeedsServiceToggle";
import { NeedsServiceChip } from "@/components/yard/NeedsServiceChip";

interface Props {
  locationId: string;
  locationName: string;
}

interface AuditTrailer {
  id: string;
  trailer_id: string;
  scac: string | null;
  seal_id: string | null;
  load_status: string | null;
  trailer_type_id: string | null;
  current_location_status: string;
  is_customer_parking: boolean;
  seal_status: string | null;
  image_path: string | null;
  needs_service: boolean | null;
  needs_service_reason: string | null;
}

export function YardAuditView({ locationId, locationName }: Props) {
  const queryClient = useQueryClient();
  const { tenantId, activeTenant } = useTenant();
  const [auditedIds, setAuditedIds] = useState<Set<string>>(new Set());
  const [auditMeta, setAuditMeta] = useState<Map<string, { timestamp: Date; userEmail: string }>>(new Map());
  const [notFoundIds, setNotFoundIds] = useState<Set<string>>(new Set());
  const [notFoundMeta, setNotFoundMeta] = useState<Map<string, { timestamp: Date }>>(new Map());
  const [selectedTrailer, setSelectedTrailer] = useState<AuditTrailer | null>(null);
  const [showConfirmDrawer, setShowConfirmDrawer] = useState(false);
  const [showFinalDrawer, setShowFinalDrawer] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [auditCompleted, setAuditCompleted] = useState(false);
  const [trailerNotFound, setTrailerNotFound] = useState(false);

  // Editable fields for detail view
  // "" = not yet chosen. "unknown" is never a selectable/persisted value.
  const [editLoadStatus, setEditLoadStatus] = useState<"empty" | "loaded" | "">("");
  const [editSealId, setEditSealId] = useState("");
  const [editSealStatus, setEditSealStatus] = useState<"intact" | "broken">("intact");
  const [editScac, setEditScac] = useState("");
  const [editTrailerTypeId, setEditTrailerTypeId] = useState("");
  const [editNeedsService, setEditNeedsService] = useState(false);
  const [editNeedsServiceReason, setEditNeedsServiceReason] = useState("");

  const { data: trailerTypes } = useTrailerTypes();

  // Fetch all trailers virtually assigned to this yard
  const { data: trailers = [], isLoading } = useQuery({
    queryKey: ["yard-audit-trailers", locationId, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("trailers")
        .select("id, trailer_id, scac, seal_id, load_status, trailer_type_id, current_location_status, is_customer_parking, seal_status, image_path, needs_service, needs_service_reason")
        .eq("current_location_id", locationId)
        .eq("current_location_status", "at_site")
        .order("trailer_id");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as AuditTrailer[];
    },
    enabled: !!locationId,
  });

  // Fetch last check-in event times for dwell time
  const auditTrailerIds = trailers.map((t) => t.id);
  const { data: dwellEvents } = useQuery({
    queryKey: ["yard-audit-dwell-events", auditTrailerIds, tenantId],
    enabled: auditTrailerIds.length > 0,
    queryFn: async () => {
      let query = supabase
        .from("trailer_events")
        .select("trailer_id, occurred_at")
        .in("trailer_id", auditTrailerIds)
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false });
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      const map = new Map<string, string>();
      for (const e of data) {
        if (!map.has(e.trailer_id)) map.set(e.trailer_id, e.occurred_at);
      }
      return map;
    },
  });

  // Fetch "found during yard audit" events
  const { data: foundEvents } = useQuery({
    queryKey: ["yard-audit-found-events", auditTrailerIds, tenantId],
    enabled: auditTrailerIds.length > 0,
    queryFn: async () => {
      let query = supabase
        .from("trailer_events")
        .select("trailer_id, occurred_at")
        .in("trailer_id", auditTrailerIds)
        .eq("event_type", "checked_in")
        .eq("notes", "Found during yard audit")
        .order("occurred_at", { ascending: false });
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      const map = new Map<string, string>();
      for (const e of data) {
        if (!map.has(e.trailer_id)) map.set(e.trailer_id, e.occurred_at);
      }
      return map;
    },
  });

  const allAudited = trailers.length > 0 && trailers.every((t) => auditedIds.has(t.id));

  // Pre-resolve signed URLs for every trailer image. The `trailer-images`
  // bucket is private, so direct public URLs no longer work — sign on the
  // client and cache by `image_path`. Hook must be called unconditionally
  // before any early returns below.
  const trailerImageUrls = useTrailerImageUrls(trailers.map((t) => t.image_path));

  const openTrailerDetail = (trailer: AuditTrailer) => {
    setSelectedTrailer(trailer);
    setEditLoadStatus(
      trailer.load_status === "empty" || trailer.load_status === "loaded"
        ? trailer.load_status
        : "",
    );
    setEditSealId(trailer.seal_id || "");
    setEditSealStatus(
      trailer.seal_status === "broken" ? "broken" : "intact"
    );
    setEditScac(trailer.scac || "");
    setEditTrailerTypeId(trailer.trailer_type_id || "");
    setEditNeedsService(!!trailer.needs_service);
    setEditNeedsServiceReason(trailer.needs_service_reason || "");
    setTrailerNotFound(false);
  };

  const getTrailerTypeLabel = (typeId: string | null) => {
    if (!typeId || !trailerTypes) return "—";
    return trailerTypes.find((tt) => tt.id === typeId)?.label || "—";
  };

  const handleCompleteTrailerAudit = async () => {
    if (!selectedTrailer) return;
    setIsProcessing(true);
    try {
      const { data: userData } = await supabase.auth.getUser();

      if (trailerNotFound) {
        // Mark as not found — log audit event with "not found" note
        await supabase.from("trailer_events").insert({
          trailer_id: selectedTrailer.id,
          event_type: "audited" as any,
          location_id: locationId,
          created_by_user_id: userData.user?.id || null,
          load_status: null,
          notes: "Trailer not found during yard audit",
        });

        const auditTime = new Date();
        const auditEmail = userData.user?.email || "Unknown";
        setAuditedIds((prev) => new Set(prev).add(selectedTrailer.id));
        setAuditMeta((prev) => new Map(prev).set(selectedTrailer.id, { timestamp: auditTime, userEmail: auditEmail }));
        setNotFoundIds((prev) => new Set(prev).add(selectedTrailer.id));
        setNotFoundMeta((prev) => new Map(prev).set(selectedTrailer.id, { timestamp: auditTime }));
        setSelectedTrailer(null);
        setShowConfirmDrawer(false);
        queryClient.invalidateQueries({ queryKey: ["yard-audit-trailers", locationId] });
        toast.success(`Trailer ${selectedTrailer.trailer_id} marked as not found`);
      } else {
        // Normal audit
        if (!editLoadStatus) {
          toast.error("Select a load status before completing the audit");
          setIsProcessing(false);
          return;
        }
        const nowIso = new Date().toISOString();
        const trailerRecordChanged =
          editNeedsService !== !!selectedTrailer.needs_service ||
          (editNeedsService && editNeedsServiceReason.trim() !== (selectedTrailer.needs_service_reason || ""));
        const { error: updateError } = await supabase
          .from("trailers")
          .update({
            load_status: editLoadStatus,
            seal_id: editSealId.trim() || null,
            scac: editScac.trim() || null,
            trailer_type_id: editTrailerTypeId || null,
            seal_status: editLoadStatus === "loaded" ? editSealStatus : null,
            needs_service: editNeedsService,
            needs_service_reason: editNeedsService ? (editNeedsServiceReason.trim() || null) : null,
            // Only bump attribution when the flag/reason actually changed,
            // so audit-only edits don't overwrite the original reporter.
            ...(trailerRecordChanged
              ? {
                  needs_service_set_at: editNeedsService ? nowIso : null,
                  needs_service_set_by: editNeedsService ? (userData.user?.id ?? null) : null,
                }
              : {}),
          })
          .eq("id", selectedTrailer.id);

        if (updateError) throw updateError;

        const { data: auditEvt } = await supabase
          .from("trailer_events")
          .insert({
            trailer_id: selectedTrailer.id,
            event_type: "audited" as any,
            location_id: locationId,
            created_by_user_id: userData.user?.id || null,
            load_status: editLoadStatus,
            seal_id: editSealId.trim() || null,
            seal_status: editLoadStatus === "loaded" ? editSealStatus : null,
            notes: `Yard audit at ${locationName}`,
          })
          .select("id")
          .single();

        // Audits: carry the trailer's most recent BoL forward onto the audit
        // event, then fan out to any active orders assigned to this trailer.
        await syncTrailerBol({
          direction: "trailer-gains-bol",
          trailerHumanId: selectedTrailer.trailer_id,
          tenantId,
          tenantSlug: activeTenant,
          carryForward: auditEvt?.id
            ? { trailerDbId: selectedTrailer.id, newEventId: auditEvt.id }
            : undefined,
        });

        const auditTime = new Date();
        const auditEmail = userData.user?.email || "Unknown";
        setAuditedIds((prev) => new Set(prev).add(selectedTrailer.id));
        setAuditMeta((prev) => new Map(prev).set(selectedTrailer.id, { timestamp: auditTime, userEmail: auditEmail }));
        setSelectedTrailer(null);
        setShowConfirmDrawer(false);
        queryClient.invalidateQueries({ queryKey: ["yard-audit-trailers", locationId] });
        toast.success(`Trailer ${selectedTrailer.trailer_id} audit complete`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to complete audit");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinalComplete = async () => {
    setIsProcessing(true);
    try {
      // Log the full yard audit completion
      await supabase.rpc("write_audit_log" as any, {
        _action: "yard_audit_complete",
        _resource_type: "location",
        _resource_id: locationId,
        _details: { location_name: locationName, trailers_audited: trailers.length },
      });

      setShowFinalDrawer(false);
      setAuditCompleted(true);
      toast.success(`Yard audit for ${locationName} complete`);
    } catch (err: any) {
      toast.error(err.message || "Failed to complete audit");
    } finally {
      setIsProcessing(false);
    }
  };

  // -- Detail Overlay --
  if (selectedTrailer) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="p-4 flex items-center gap-3 border-b">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedTrailer(null)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Audit Trailer {selectedTrailer.trailer_id}</h1>
        </div>

        <div className="flex-1 overflow-auto p-4 pb-28 space-y-4">
          {/* Editable fields */}
          <div className={`space-y-4 transition-opacity ${trailerNotFound ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="space-y-1.5">
              <Label className="text-xs">Trailer Type</Label>
              <Select value={editTrailerTypeId} onValueChange={setEditTrailerTypeId} disabled={trailerNotFound}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trailer type" />
                </SelectTrigger>
                <SelectContent>
                  {trailerTypes?.map((tt) => (
                    <SelectItem key={tt.id} value={tt.id}>{tt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">SCAC</Label>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                value={editScac}
                onChange={(e) => setEditScac(e.target.value.toUpperCase())}
                placeholder="e.g. JBHT, SNLU..."
                disabled={trailerNotFound}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Load Status</Label>
              <div className="grid grid-cols-2 gap-2">
                {(["empty", "loaded"] as const).map((status) => (
                  <Button
                    key={status}
                    type="button"
                    variant={editLoadStatus === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditLoadStatus(status)}
                    className="capitalize"
                    disabled={trailerNotFound}
                  >
                    {status}
                  </Button>
                ))}
              </div>
            </div>

            {editLoadStatus === "loaded" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Seal ID</Label>
                  <input
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    value={editSealId}
                    onChange={(e) => setEditSealId(e.target.value)}
                    placeholder="Enter seal number..."
                    disabled={trailerNotFound}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Seal Status</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={editSealStatus === "intact" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEditSealStatus("intact")}
                      disabled={trailerNotFound}
                    >
                      Intact
                    </Button>
                    <Button
                      type="button"
                      variant={editSealStatus === "broken" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEditSealStatus("broken")}
                      disabled={trailerNotFound}
                    >
                      Broken
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Read-only info */}
            <div className="border-t pt-4 space-y-0">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="text-sm font-medium">{locationName}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="text-sm font-medium capitalize">{selectedTrailer.current_location_status.replace("_", " ")}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Dwell Time</span>
                <span className="text-sm font-medium">{formatDwellTime(dwellEvents?.get(selectedTrailer.id))}</span>
              </div>
            </div>
          </div>

          {/* Needs Service */}
          <div className={trailerNotFound ? "opacity-40 pointer-events-none" : ""}>
            <NeedsServiceToggle
              needsService={editNeedsService}
              needsServiceReason={editNeedsServiceReason}
              onNeedsServiceChange={setEditNeedsService}
              onReasonChange={setEditNeedsServiceReason}
              disabled={trailerNotFound}
            />
          </div>

          {/* Trailer not found checkbox */}
          <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
            <Checkbox
              id="trailer-not-found"
              checked={trailerNotFound}
              onCheckedChange={(checked) => setTrailerNotFound(checked === true)}
            />
            <div>
              <label htmlFor="trailer-not-found" className="text-sm font-medium cursor-pointer">
                Trailer not found
              </label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mark this trailer as missing from the yard
              </p>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
          <Button
            className="w-full"
            size="lg"
            disabled={
              !trailerNotFound &&
              (!editLoadStatus || (editNeedsService && !editNeedsServiceReason.trim()))
            }
            onClick={() => setShowConfirmDrawer(true)}
          >
            Complete Trailer Audit
          </Button>
        </div>

        {/* Confirmation half-sheet */}
        <Drawer open={showConfirmDrawer} onOpenChange={setShowConfirmDrawer}>
          <DrawerContent>
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-semibold">Complete audit for Trailer {selectedTrailer.trailer_id}?</h3>
              <p className="text-sm text-muted-foreground">
                This will confirm the current data for Trailer {selectedTrailer.trailer_id} at {locationName} and mark it as audited.
              </p>
            </div>
            <DrawerFooter className="flex-row gap-3">
              <Button
                variant="outline"
                className="flex-1"
                size="lg"
                onClick={() => setShowConfirmDrawer(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                size="lg"
                onClick={handleCompleteTrailerAudit}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  "Confirm"
                )}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  // -- List View --
  const unaudited = trailers.filter((t) => !auditedIds.has(t.id));
  const audited = trailers.filter((t) => auditedIds.has(t.id));

  const getTrailerImageUrl = (imagePath: string | null) => {
    if (!imagePath) return null;
    return trailerImageUrls[imagePath] ?? null;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-auto p-4 space-y-4 pb-24">
        <div className="rounded-lg border bg-card p-4">
          <p className="text-2xl font-bold tabular-nums">{unaudited.length}</p>
          <p className="text-sm text-muted-foreground">trailers to audit</p>
        </div>

        {isLoading ? (
          <div className="rounded-lg bg-muted/50 p-8 text-center text-sm text-muted-foreground animate-pulse">
            Loading trailers...
          </div>
        ) : trailers.length === 0 ? (
          <div className="rounded-lg bg-muted/50 p-8 text-center text-sm text-muted-foreground">
            No trailers assigned to this yard.
          </div>
        ) : (
          <>
            {/* Unaudited trailers */}
            {unaudited.length > 0 && (
              <div className="space-y-2">
                {unaudited.map((trailer) => {
                  const trailerType = trailerTypes?.find((tt) => tt.id === trailer.trailer_type_id);
                  return (
                    <button
                      key={trailer.id}
                      onClick={() => openTrailerDetail(trailer)}
                      className="w-full text-left rounded-lg border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all active:scale-[0.98]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold font-mono text-base">{trailer.trailer_id}</p>
                            {dwellEvents?.get(trailer.id) && (() => {
                              const occurredAt = dwellEvents.get(trailer.id);
                              const isOver = isDwellOverThreshold(occurredAt);
                              return (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isOver ? "bg-destructive/10 text-destructive border border-destructive/20" : "bg-muted text-muted-foreground"}`}>
                                  <Clock className="h-3 w-3" />
                                  {formatDwellTime(occurredAt)}
                                </span>
                              );
                            })()}
                            {trailer.is_customer_parking && (
                              <Badge variant="outline" className="text-xs w-fit">Customer Parking</Badge>
                            )}
                            <NeedsServiceChip
                              needsService={trailer.needs_service}
                              reason={trailer.needs_service_reason}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {[trailer.scac, trailerType?.label, trailer.load_status && trailer.load_status !== "unknown" ? trailer.load_status : null].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        {foundEvents?.get(trailer.id) && (
                          <div className="flex flex-col items-end shrink-0">
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                              <Search className="h-3 w-3" />
                              Found
                            </span>
                          </div>
                        )}
                        {(() => {
                          const url = getTrailerImageUrl(trailer.image_path);
                          return url ? (
                            <img src={url} alt={trailer.trailer_id} className="h-12 w-12 rounded object-cover shrink-0" />
                          ) : (
                            <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0">
                              <Container className="h-5 w-5 text-muted-foreground" />
                            </div>
                          );
                        })()}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Audited trailers */}
            {audited.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-base font-semibold text-muted-foreground">Audited</h3>
                {audited.map((trailer) => {
                  const meta = auditMeta.get(trailer.id);
                  const trailerType = trailerTypes?.find((tt) => tt.id === trailer.trailer_type_id);
                  return (
                    <div
                      key={trailer.id}
                      className="w-full text-left rounded-lg border bg-card p-4 opacity-75"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold font-mono text-base">{trailer.trailer_id}</p>
                            {dwellEvents?.get(trailer.id) && (() => {
                              const occurredAt = dwellEvents.get(trailer.id);
                              const isOver = isDwellOverThreshold(occurredAt);
                              return (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isOver ? "bg-destructive/10 text-destructive border border-destructive/20" : "bg-muted text-muted-foreground"}`}>
                                  <Clock className="h-3 w-3" />
                                  {formatDwellTime(occurredAt)}
                                </span>
                              );
                            })()}
                            {trailer.is_customer_parking && (
                              <Badge variant="outline" className="text-xs w-fit">Customer Parking</Badge>
                            )}
                            <NeedsServiceChip
                              needsService={trailer.needs_service}
                              reason={trailer.needs_service_reason}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {[trailer.scac, trailerType?.label, trailer.load_status && trailer.load_status !== "unknown" ? trailer.load_status : null].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          {notFoundIds.has(trailer.id) && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20">
                              <AlertTriangle className="h-3 w-3" />
                              Not found
                            </span>
                          )}
                          {!notFoundIds.has(trailer.id) && foundEvents?.get(trailer.id) && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                              <Search className="h-3 w-3" />
                              Found
                            </span>
                          )}
                          {meta && (
                            <p className="text-xs text-emerald-600 font-medium mt-1">
                              Audited on {format(meta.timestamp, "MMM d")} · {format(meta.timestamp, "h:mm a")} · {meta.userEmail}
                            </p>
                          )}
                        </div>
                        {(() => {
                          const url = getTrailerImageUrl(trailer.image_path);
                          return url ? (
                            <img src={url} alt={trailer.trailer_id} className="h-12 w-12 rounded object-cover shrink-0" />
                          ) : (
                            <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0">
                              <Container className="h-5 w-5 text-muted-foreground" />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Progress bar + Complete Audit button */}
      {trailers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t space-y-2">
          {!auditCompleted && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{auditedIds.size} / {trailers.length}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(auditedIds.size / trailers.length) * 100}%` }}
                />
              </div>
            </>
          )}
          <Button
            className="w-full"
            size="lg"
            disabled={!allAudited || auditCompleted}
            onClick={() => setShowFinalDrawer(true)}
          >
            {auditCompleted ? "Yard audit completed" : `Complete Yard Audit (${auditedIds.size}/${trailers.length})`}
          </Button>
        </div>
      )}

      {/* Final completion drawer */}
      <Drawer open={showFinalDrawer} onOpenChange={setShowFinalDrawer}>
        <DrawerContent>
          <div className="p-6 space-y-3">
            <h3 className="text-lg font-semibold">Complete {locationName} yard audit?</h3>
            <p className="text-sm text-muted-foreground">
              This will finalize the audit for {locationName} with {trailers.length} trailers reviewed.
            </p>
          </div>
          <DrawerFooter className="flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              size="lg"
              onClick={() => setShowFinalDrawer(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              size="lg"
              onClick={handleFinalComplete}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Finalizing...</>
              ) : (
                "Confirm"
              )}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
